// HustleFlywheel.jsx
import React from 'react';

const HustleFlywheel = () => {
  const merchantBenefits = [
    "Discover freelance, remote, startup and local earning opportunities.",
    "Build multiple income streams instead of depending on a single job.",
    "Showcase skills and connect directly with opportunity providers.",
    "Suitable for students, homemakers, professionals and retirees.",
    "Experiment safely with side hustles before making career decisions.",
    "Collaborate with other members on projects and startups.",
    "Develop entrepreneurial thinking through practical experience.",
    "Strengthen your portfolio through real-world work.",
    "Improve confidence, communication and business skills.",
    "Transform hobbies and passions into sustainable income."
  ];

  const customerBenefits = [
  "Creates a continuously growing opportunity marketplace.",
  "Encourages collaboration instead of unhealthy competition.",
  "Supports local and global entrepreneurship.",
  "Helps startups discover motivated contributors.",
  "Provides visibility to freelancers and skilled individuals.",
  "Connects people with complementary talents.",
  "Encourages innovation through practical experimentation.",
  "Strengthens community-driven economic growth.",
  "Builds long-term member engagement through opportunities.",
  "Creates a self-sustaining opportunity flywheel."
];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans">
      {/* Header */}
      <div className="pt-6 pb-4 px-5 text-center border-b border-purple-500/20">
        <h1 className="text-xl lg:text-2xl font-bold mb-3 bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent">
          💼 Think5Whys Hustles
        </h1>
        <p className="text-sm lg:text-lg text-purple-300">Discover Opportunities • Build Skills • Earn Income • Create Impact</p>
      </div>

      {/* Introduction */}
      <div className="max-w-4xl mx-auto px-4 py-1 text-center">
        <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-3xl px-4 py-2 lg:px-5 lg:py-4">
          <p className="text-sm lg:text-lg leading-relaxed text-purple-100">
            Think5Whys Hustles is a career and opportunity ecosystem designed for students, freelancers, homemakers, 
            professionals, retirees, startups, and aspiring entrepreneurs. Instead of waiting for opportunities, members 
            continuously discover work, showcase their abilities, collaborate with others, and build sustainable income streams. 
            The platform encourages experimentation, lifelong learning, and multiple income sources while supporting the 
            GrowthLoop philosophy of continuous personal and professional development.
          </p>
        </div>
      </div>

      {/* Benefits to Merchants */}
      <div className="max-w-5xl mx-auto px-4 py-4">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 flex items-center gap-3">
          <span>🛍️</span> Benefits to Opportunity Creators / Service Seekers
        </h2>
        <div className="grid lg:grid-cols-2 gap-1">
          {merchantBenefits.map((benefit, i) => (
            <div key={i} className="bg-zinc-900/80 hover:bg-zinc-800 border border-purple-500/20 rounded-2xl px-4 py-1 transition-all hover:border-purple-400 flex gap-4">
              <span className="text-xs lg:text-sm">✅</span>
              <p className="text-xs lg:text-sm leading-tight text-purple-100">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits to Customers */}
      <div className="max-w-5xl mx-auto px-4 py-4 bg-black/40">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 flex items-center gap-3">
          <span>🌟</span> Benefits to Hustlers
        </h2>
        <div className="grid lg:grid-cols-2 gap-1">
          {customerBenefits.map((benefit, i) => (
            <div key={i} className="bg-zinc-900/80 hover:bg-zinc-800 border border-pink-500/20 rounded-2xl px-4 py-1 transition-all hover:border-pink-400 flex gap-4">
              <span className="text-xs lg:text-sm">⭐</span>
              <p className="text-xs lg:text-sm leading-tight text-pink-100">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* The Flywheel Section */}
      <div className="max-w-5xl mx-auto px-5 py-4">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-10 text-center flex items-center justify-center gap-3">
          <span>🔄</span> THE FLYWHEEL
        </h2>

        <div className="space-y-2 lg:space-y-3 relative before:absolute before:left-8 lg:before:left-1/2 before:top-0 before:bottom-0 before:w-1 before:bg-gradient-to-b before:from-purple-500 before:via-pink-500 before:to-yellow-400 before:opacity-30">
          {[
            ["Join", "Become a Think5Whys member and activate your Hustles journey.", "🚀"],
            ["Explore", "Browse freelance, startup, remote work, retail, boutique and entrepreneurial opportunities.", "🔍"],
            ["Learn", "Develop practical skills, communication and business confidence through real experiences.", "📚"],
            ["Apply", "Start freelancing, collaborate on projects or launch your own venture.", "💼"],
            ["Earn", "Generate income while building experience, credibility and confidence.", "💰"],
            ["Grow", "Expand your skills, portfolio, professional network and reputation.", "📈"],
            ["Create", "Launch startups, boutiques, franchises or create new opportunities for others.", "🏗️"],
            ["Mentor", "Share knowledge and inspire the next generation of hustlers.", "🤝"],
            ["Expand", "Successful members strengthen the entire Think5Whys opportunity ecosystem.", "🌍"],
            ["Flywheel", "More opportunities → More skilled members → More businesses → More employment → More opportunities.", "♾️"]
          ].map((step, index) => (
            <div key={index} className="relative flex lg:items-center gap-2 lg:even:flex-row-reverse">
              <div className="w-8 h-8 lg:w-10 lg:h-10 flex-shrink-0 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center text-xl lg:text-2xl shadow-xl border-4 border-black z-10">
                {step[2]}
              </div>
              <div className="bg-zinc-900/90 border border-white/10 rounded-2xl px-4 py-1 lg:px-8 lg:py-2 flex-1">
                <h3 className="text-sm lg:text-lg font-bold mb-2 text-purple-300">{step[0]}</h3>
                <p className="text-purple-100 text-xs lg:text-sm leading-relaxed">{step[1]}</p>
              </div>
            </div>
          ))}
        </div>

        {/* <div className="mt-10 text-center bg-gradient-to-r from-emerald-900/50 to-cyan-900/50 border border-emerald-500/30 rounded-3xl p-6">
          <p className="text-emerald-300 font-medium">
            No selling commission charged to sellers • Subscription starts at <span className="font-bold text-white">₹50</span> first quarter (India + GST)
          </p>
        </div> */}
      </div>

      {/* Recommendation Section */}
      <div className="max-w-4xl mx-auto px-4 py-4 bg-gradient-to-b from-transparent via-purple-950/30 to-transparent">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 text-center">💡 Recommendation</h2>
        <div className="bg-zinc-900/80 border border-yellow-400/20 rounded-3xl px-4 py-1 lg:px-6 lg:py-2 text-center lg:text-left">
          <p className="text-sm lg:text-lg leading-relaxed text-yellow-100">
            The future belongs to people who continuously learn, adapt and build multiple sources of value. 
            Instead of waiting for the perfect opportunity, start small, experiment frequently and treat every project 
            as a stepping stone toward larger goals. Every skill you develop compounds into future opportunities.
          </p>
          <p className="text-sm lg:text-lg mt-2 text-purple-300">
            Think5Whys Hustles encourages lifelong employability, entrepreneurship and collaboration.
             Whether your ambition is to freelance, build a startup, create a digital boutique, 
             launch a franchise or simply earn additional income, consistent learning and practical execution will keep 
             your GrowthLoop moving forward.
          </p>
        </div>
      </div>

      <footer className="text-center py-4 text-xs text-purple-500 border-t border-white/10">
        Think5Whys Hustles • Discover Opportunities • Build Skills • Earn Income • Create Employment 💜
      </footer>
    </div>
  );
};

export default HustleFlywheel;